// Type-only: keeps this module free of runtime imports so the framework-agnostic
// remote lib can value-import HANDSHAKE_TIMEOUT_MS without pulling in Angular.
import type { SharedAppSettings } from '@navstation-portal/shared/data-model/remote-app-config';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'failed';

/**
 * Budget for each leg of the handshake: host waiting for READY, remote waiting
 * for CONFIG, and host waiting for ACK. Shared so both sides stay in step.
 *
 * Sized for a full remote app boot on prod, which is far slower than local. Kept
 * uniform across the legs deliberately — a tighter budget on the CONFIG/ACK legs
 * would fail a healthy app whenever that round-trip is deferred, and we can't yet
 * guarantee the ordering in prod.
 */
export const HANDSHAKE_TIMEOUT_MS = 30000;

export interface AckPayload {
  success: boolean;
}

export interface StandardPayload {
  action: string;
  data?: unknown;
}

export interface ConfigPayload extends SharedAppSettings {
  headless?: boolean;
}

export interface TitlePayload {
  title: string;
}

export interface MessagePayloadMap {
  ready: undefined;
  config: ConfigPayload;
  ack: AckPayload;
  standard: StandardPayload;
  settings: SharedAppSettings;
  title: TitlePayload;
}

export type MessageType = keyof MessagePayloadMap;
export type PayloadOf<T extends MessageType> = MessagePayloadMap[T];

export interface TypedMessage<T extends MessageType> {
  id: string;
  iframeId: string;
  timestamp: number;
  type: T;
  payload: PayloadOf<T>;
}

export type Message = {
  [K in MessageType]: TypedMessage<K>;
}[MessageType];
